import { mockDevices } from "./devices"
import { mockId, mulberry32, pick, pickWeighted, randInt } from "./random"

const SEVERITIES = ["critical", "warning", "info"] as const
type Severity = (typeof SEVERITIES)[number]

const MESSAGES: Record<Severity, string[]> = {
  critical: [
    "Sigorta atladı — faz 2",
    "Aşırı gerilim koruması devrede",
    "Batarya hücresi arızalı",
    "Güç modülü kapandı",
    "İletişim tamamen kesildi",
    "İnvertör hata koduyla durdu: F45",
  ],
  warning: [
    "İmza doğrulama süresi aşıldı",
    "Sıcaklık üst limite yaklaşıyor",
    "Batarya şarj oranı %15 altına düştü",
    "Yaklaşık kapasite azalması tespit edildi",
    "Firmware güncellemesi bekleniyor",
    "Zayıf sinyal — yeniden bağlanıyor",
  ],
  info: [
    "Cihaz yeniden başlatıldı",
    "Konfigürasyon başarıyla uygulandı",
    "Günlük rapor gönderildi",
    "Bakım bildirimi: filtre temizliği",
    "Yeni firmware kullanılabilir",
    "Enerji üretimi normal seviyede",
  ],
}

export interface MockAlarm {
  id: string
  deviceId: string
  siteId: string
  setupDeviceId: string
  deviceNotificationTypeId: string
  timestamp: string
  additionalData: string
  isProcessed: boolean
  isDeleted: boolean
  created: string
  deviceManufacturer: string
  siteName: string
  deviceName: string
  displayMessage: string
  severity: Severity
  sourceDevice: string
  rawRegisterCode: string | null
  isGeneric: boolean
  key: string
  detailedDescription: string
}

const ALARMS_SEED = 9001

function makeAlarmWith(
  rng: () => number,
  opts: { severity?: Severity; timestamp?: string; isProcessed?: boolean } = {},
): MockAlarm {
  const device = mockDevices[Math.floor(rng() * mockDevices.length)]
  if (!device) {
    throw new Error("No devices to reference in alarm fixture")
  }
  const severity =
    opts.severity ??
    pickWeighted(rng, [
      { value: "critical" as Severity, weight: 10 },
      { value: "warning" as Severity, weight: 25 },
      { value: "info" as Severity, weight: 65 },
    ])
  const messages = MESSAGES[severity]
  const message = pick(rng, messages)
  const ts = opts.timestamp ?? new Date().toISOString()
  const isProcessed = opts.isProcessed ?? (severity === "info" ? rng() < 0.6 : rng() < 0.3)
  return {
    id: mockId(rng, "alarm"),
    deviceId: device.id,
    siteId: device.siteId,
    setupDeviceId: mockId(rng, "setup"),
    deviceNotificationTypeId: mockId(rng, "type"),
    timestamp: ts,
    additionalData: JSON.stringify({
      code: `REG-${randInt(rng, 100, 999)}`,
      raw: `0x${Math.floor(rng() * 0xffffff)
        .toString(16)
        .padStart(6, "0")
        .toUpperCase()}`,
    }),
    isProcessed,
    isDeleted: false,
    created: ts,
    deviceManufacturer: device.manufacturer,
    siteName: device.siteName,
    deviceName: `${device.manufacturer} ${device.modelNumber}`,
    displayMessage: message,
    severity,
    sourceDevice: device.serialNumber,
    rawRegisterCode: severity !== "info" ? `R${randInt(rng, 1, 99)}` : null,
    isGeneric: severity === "info",
    key: `${severity.toUpperCase()}_${randInt(rng, 1000, 9999)}`,
    detailedDescription: `${message}. Cihaz: ${device.serialNumber}. Konum: ${device.siteName}.`,
  }
}

function generate(): MockAlarm[] {
  const rng = mulberry32(ALARMS_SEED)
  const alarms: MockAlarm[] = []
  const now = Date.now()
  for (let i = 0; i < 400; i++) {
    const daysAgo = randInt(rng, 0, 7)
    const secondsAgo = randInt(rng, 0, 86400)
    const ts = new Date(now - daysAgo * 86400_000 - secondsAgo * 1000).toISOString()
    alarms.push(makeAlarmWith(rng, { timestamp: ts }))
  }
  // Timestamp desc sırala
  alarms.sort((a, b) => (a.timestamp < b.timestamp ? 1 : -1))
  return alarms
}

/**
 * In-memory alarm store. MSW handler ve mock SignalR bu tek array üzerinde çalışır.
 */
export const mockAlarms: MockAlarm[] = generate()

let streamRng: (() => number) | null = null
function getStreamRng() {
  if (!streamRng) {
    streamRng = mulberry32(Date.now() & 0xffffffff)
  }
  return streamRng
}

/**
 * Mock SignalR sürücüsü için yeni alarm üretir — yüksek severity oranı, şimdiki
 * zaman damgası, henüz onaylanmamış. Aynı zamanda in-memory store'a prepend eder
 * ki `GET /DeviceNotifications` de sonraki istekte onu görsün.
 */
export function makeRandomAlarm(): MockAlarm {
  const rng = getStreamRng()
  const severity = pickWeighted(rng, [
    { value: "critical" as Severity, weight: 25 },
    { value: "warning" as Severity, weight: 45 },
    { value: "info" as Severity, weight: 30 },
  ])
  const alarm = makeAlarmWith(rng, {
    severity,
    timestamp: new Date().toISOString(),
    isProcessed: false,
  })
  mockAlarms.unshift(alarm)
  return alarm
}

export function markAlarmRead(id: string): MockAlarm | undefined {
  const a = mockAlarms.find((x) => x.id === id)
  if (a) {
    a.isProcessed = true
  }
  return a
}
